import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companiesAPI } from '../../lib/api';
import { formatCNPJ, cleanCNPJ } from '../../lib/documentUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Building2 } from 'lucide-react';

export default function CompanySetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    cnpj: '',
    plano: 'basic',
    selo_inclusivo: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Limpar CNPJ (suporta alfanumérico NT 2026.004)
    const cnpjClean = cleanCNPJ(form.cnpj);
    if (cnpjClean.length !== 14) {
      toast.error('CNPJ deve ter 14 caracteres');
      return;
    }

    setLoading(true);
    try {
      await companiesAPI.create({
        ...form,
        cnpj: cnpjClean
      });
      toast.success('Empresa cadastrada com sucesso!');
      navigate('/company');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-2xl mx-auto" data-testid="company-setup">
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-cyan-600" />
            </div>
            <CardTitle className="text-2xl">Cadastro da Empresa</CardTitle>
            <CardDescription>
              Complete os dados da sua empresa para começar a recrutar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da Empresa *</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Nome da empresa"
                  required
                  data-testid="company-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ *</Label>
                <Input
                  id="cnpj"
                  value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: formatCNPJ(e.target.value) })}
                  placeholder="AB.C12.345/6001-99"
                  required
                  data-testid="company-cnpj-input"
                />
                <p className="text-xs text-slate-500">
                  Novo formato alfanumérico: AA.AAA.AAA/AAAA-00
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="selo_inclusivo" className="font-medium">
                    Selo Inclusivo
                  </Label>
                  <p className="text-sm text-slate-600">
                    Empresa comprometida com diversidade e inclusão
                  </p>
                </div>
                <Switch
                  id="selo_inclusivo"
                  checked={form.selo_inclusivo}
                  onCheckedChange={(checked) => setForm({ ...form, selo_inclusivo: checked })}
                  data-testid="company-inclusive-switch"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                disabled={loading}
                data-testid="company-setup-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Completar Cadastro'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
